import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import VideoCard from "@/components/base/VideoCard";
import { categories, gossipItems } from "@/mocks/gossip";
import { usePageMeta } from "@/hooks/usePageMeta";

type SortKey = "latest" | "hot";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const [activeTag, setActiveTag] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");

  const categoryItems = useMemo(
    () => gossipItems.filter((item) => item.category === slug),
    [slug]
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    categoryItems.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return ["全部", ...Array.from(set)];
  }, [categoryItems]);

  const list = useMemo(() => {
    const filtered = activeTag === "全部" ? categoryItems : categoryItems.filter((item) => item.tags.includes(activeTag));
    return [...filtered].sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return a.date < b.date ? 1 : -1;
    });
  }, [categoryItems, activeTag, sort]);

  usePageMeta({
    title: category
      ? `${category.name} - 视频吃瓜${category.name}合集 | 瓜田影像社`
      : "分类不存在 | 瓜田影像社",
    description: category
      ? `${category.description} 视频吃瓜${category.name}频道持续更新热门现场画面，含实拍、完整版与分直拍，点开即可直击现场。`
      : "该分类暂不存在，请返回首页浏览视频吃瓜的六大现场分类。",
    keywords: category
      ? `视频吃瓜${category.name},视频吃瓜,${category.name}视频,现场${category.name}`
      : "视频吃瓜,现场视频分类",
    path: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `视频吃瓜${category.name}合集`,
            description: category.description,
            url: `https://guatian-live.example.com/category/${category.slug}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://guatian-live.example.com/" },
              {
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: `https://guatian-live.example.com/category/${category.slug}`,
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `视频吃瓜${category.name}内容列表`,
            itemListElement: list.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              url: `https://guatian-live.example.com/video/${item.id}`,
            })),
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-600">
            <i className="ri-error-warning-line w-7 h-7 flex items-center justify-center"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            该分类可能已调整，返回首页即可浏览实拍、第一现场、完整版、录像、路透与分直拍六大板块。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: category.name }]} />
            <div className="flex flex-wrap items-start gap-5">
              <span className="inline-flex items-center justify-center w-14 h-14 flex-shrink-0 rounded-lg bg-primary-500 text-background-50">
                <i className={`${category.icon} w-7 h-7 flex items-center justify-center`}></i>
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  视频吃瓜{category.name}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.description}</p>
                <p className="mt-2 text-xs text-foreground-500">
                  本分类共收录 <strong className="text-primary-600">{categoryItems.length}</strong> 段现场内容，持续更新中。
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">切换分类：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm cursor-pointer transition-colors ${
                    cat.slug === category.slug
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    activeTag === tag
                      ? "bg-secondary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <div className="inline-flex items-center rounded-full bg-background-100 p-1">
              {(
                [
                  { key: "latest", label: "最新发布" },
                  { key: "hot", label: "按热度" },
                ] as { key: SortKey; label: string }[]
              ).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    sort === option.key ? "bg-background-50 text-foreground-950 font-medium" : "text-foreground-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {list.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <p className="text-sm text-foreground-600">该标签下暂时没有内容，换个标签看看。</p>
            </div>
          )}

          <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              关于「视频吃瓜{category.name}」
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {category.description}
              我们坚持只保留现场画面本身，不添加解说、不做片段拼接，并在每个视频详情页标注时长与发布时间，
              方便你判断内容是否符合预期。如果你手上有值得收录的{category.name}素材，欢迎通过
              <Link to="/about#contact" className="mx-1 cursor-pointer text-primary-600 hover:text-primary-700">
                投稿表单
              </Link>
              告诉我们。
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}